import {
  formatDate,
  formatRequestLoadStatus,
  formatWeight,
  statusClass,
} from './kioskFormatters';
import {
  getRequestProductWeight,
  isCompletedRequest,
} from './kioskTransforms';

function formatRequestDirection(direction) {
  if (direction === 'Inbound') {
    return 'Inbound';
  }

  if (direction === 'Outbound') {
    return 'Outbound';
  }

  return 'Unknown';
}

function getReplacementSourceLoadName(request, requestById, requestLoadById) {
  if (!request?.replacesRequestId) {
    return null;
  }

  const sourceRequest = requestById.get(request.replacesRequestId);
  if (!sourceRequest) {
    return request.replacesRequestId;
  }

  const sourceLoadId = sourceRequest?.requestLoadId;
  if (!sourceLoadId) {
    return sourceRequest.requestId;
  }

  const sourceLoadName = requestLoadById.get(sourceLoadId)?.name ?? sourceLoadId;
  return `${sourceRequest.requestId} (${sourceLoadName})`;
}

function normalizeProductName(value) {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeCanonicalProductId(value) {
  return String(value ?? '').trim().toLowerCase();
}

function getRequestProductQuantity(request) {
  const quantity = Number(request?.palletQuantity);
  return Number.isFinite(quantity) ? Math.max(quantity, 0) : 0;
}

function hasMatchedReadings(request) {
  return request?.requestStatus === 'AwaitingConfirmation'
    && request?.selectedScannerReadingId != null
    && request?.selectedScaleReadingId != null;
}

function getProgressPercent(actual, target) {
  const normalizedActual = Number(actual) || 0;
  const normalizedTarget = Number(target) || 0;
  if (normalizedTarget <= 0) {
    return 0;
  }

  return Math.min(Math.max((normalizedActual / normalizedTarget) * 100, 0), 100);
}

function getBookedItemWeight(item) {
  if (item?.calculatedGoodsWeightKg != null) {
    const calculatedGoodsWeight = Number(item.calculatedGoodsWeightKg);
    if (Number.isFinite(calculatedGoodsWeight)) {
      return calculatedGoodsWeight;
    }
  }

  if (item?.totalWeight != null) {
    const totalWeight = Number(item.totalWeight);
    if (Number.isFinite(totalWeight)) {
      return totalWeight;
    }
  }

  if (item?.weightPerPack != null && item?.quantityPacks != null) {
    const weightPerPack = Number(item.weightPerPack);
    const quantity = Number(item.quantityPacks);
    if (Number.isFinite(weightPerPack) && Number.isFinite(quantity)) {
      return weightPerPack * quantity;
    }
  }

  return null;
}

function getBookedWeight(detailLoad) {
  const bookedWeight = Number(detailLoad?.bookedWeightKg);
  if (detailLoad?.bookedWeightKg != null && Number.isFinite(bookedWeight)) {
    return bookedWeight;
  }

  const bookedItems = Array.isArray(detailLoad?.bookedItems) ? detailLoad.bookedItems : [];
  const bookedItemWeights = bookedItems
    .map(getBookedItemWeight)
    .filter((weight) => weight != null);

  return bookedItemWeights.length > 0
    ? bookedItemWeights.reduce((total, weight) => total + weight, 0)
    : null;
}

function BookedItemProgressBar({ label, percent, color, isOverTarget }) {
  const roundedPercent = Math.round(percent);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={roundedPercent}
      aria-valuetext={`${roundedPercent}%`}
      style={{
        height: '0.55rem',
        marginTop: '0.45rem',
        borderRadius: '999px',
        background: '#dce9ef',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: `${percent}%`,
          height: '100%',
          borderRadius: '999px',
          background: isOverTarget ? '#c0392b' : percent >= 100 ? '#0f8a4b' : color,
        }}
      />
    </div>
  );
}

function buildRelatedRequests(detailLoad, detailRequests, requests, requestLoads) {
  const baseRequests = Array.isArray(detailRequests) ? detailRequests : [];
  if (!detailLoad?.loadId) {
    return baseRequests;
  }

  const allRequests = Array.isArray(requests) ? requests : [];
  const allLoads = Array.isArray(requestLoads) ? requestLoads : [];
  const relevantLoadIds = new Set([detailLoad.loadId]);

  if (detailLoad.direction === 'Outbound') {
    allLoads.forEach((requestLoad) => {
      if (requestLoad?.linkedOutboundLoadId === detailLoad.loadId) {
        relevantLoadIds.add(requestLoad.loadId);
      }
    });
  }

  const requestById = new Map();
  baseRequests.forEach((request) => {
    if (request?.requestId) {
      requestById.set(request.requestId, request);
    }
  });

  allRequests.forEach((request) => {
    if (!request?.requestId || !relevantLoadIds.has(request.requestLoadId)) {
      return;
    }

    const existing = requestById.get(request.requestId);
    if (!existing) {
      requestById.set(request.requestId, request);
      return;
    }

    requestById.set(request.requestId, {
      ...request,
      ...existing,
      requestStatus: existing.requestStatus ?? request.requestStatus,
      confirmationStatus: existing.confirmationStatus ?? request.confirmationStatus,
      requestDirection: existing.requestDirection ?? request.requestDirection,
      palletProductName: existing.palletProductName ?? request.palletProductName,
      palletCanonicalProductId: existing.palletCanonicalProductId ?? request.palletCanonicalProductId,
      palletQuantity: existing.palletQuantity ?? request.palletQuantity,
      palletWeight: existing.palletWeight ?? request.palletWeight,
      calculatedGoodsWeightKg: existing.calculatedGoodsWeightKg ?? request.calculatedGoodsWeightKg,
      scaleValue: existing.scaleValue ?? request.scaleValue,
      numericScaleValue: existing.numericScaleValue ?? request.numericScaleValue,
    });
  });

  return [...requestById.values()].sort((left, right) => (
    new Date(right?.issuedAtUtc ?? 0) - new Date(left?.issuedAtUtc ?? 0)
  ));
}

function buildProgressRequests(detailLoad, detailRequests, requests, requestLoads) {
  if (detailLoad?.direction !== 'Outbound') {
    return Array.isArray(detailRequests) ? detailRequests : [];
  }

  return buildRelatedRequests(detailLoad, detailRequests, requests, requestLoads);
}

function buildBookedItemProgress(bookedItems, requests, detailLoad) {
  const normalizedBookedItems = Array.isArray(bookedItems) ? bookedItems : [];
  const normalizedRequests = Array.isArray(requests) ? requests : [];
  if (normalizedBookedItems.length === 0) {
    return [];
  }

  const groupedBookedItems = normalizedBookedItems.reduce((groups, item, index) => {
    const canonicalProductId = normalizeCanonicalProductId(item?.canonicalProductId);
    const packagePerPalletValue = item?.packagePerPallet != null
      ? Number(item.packagePerPallet)
      : null;
    const packagePerPallet = Number.isFinite(packagePerPalletValue) ? packagePerPalletValue : null;
    const groupKey = canonicalProductId || `missing-canonical-product-${index}`;
    const group = groups.get(groupKey) ?? {
      id: groupKey,
      canonicalProductId,
      productNames: new Map(),
      brandNames: new Map(),
      sizes: new Map(),
      packagePerPallet,
      palletQuantity: null,
      bookedQuantity: 0,
      targetWeight: 0,
      hasTargetWeight: false,
      measuredQuantity: 0,
      measuredWeight: 0,
    };
    const productName = String(item?.productName ?? '').trim() || 'Unnamed product';
    group.productNames.set(normalizeProductName(productName), productName);
    const brandName = String(item?.brandName ?? '').trim();
    if (brandName) {
      group.brandNames.set(normalizeProductName(brandName), brandName);
    }
    const size = String(item?.size ?? '').trim();
    if (size) {
      group.sizes.set(normalizeProductName(size), size);
    }
    group.bookedQuantity += Number(item?.quantityPacks) || 0;
    const itemTargetWeight = getBookedItemWeight(item);
    if (itemTargetWeight != null) {
      group.targetWeight += itemTargetWeight;
      group.hasTargetWeight = true;
    }
    if (item?.palletQuantity != null && Number.isFinite(Number(item.palletQuantity))) {
      group.palletQuantity = (group.palletQuantity ?? 0) + Number(item.palletQuantity);
    }
    groups.set(groupKey, group);
    return groups;
  }, new Map());

  const groups = [...groupedBookedItems.values()];
  normalizedRequests
    .filter((request) => isCompletedRequest(request) || hasMatchedReadings(request))
    .forEach((request) => {
      const requestCanonicalProductId = normalizeCanonicalProductId(request?.palletCanonicalProductId);
      if (!requestCanonicalProductId) {
        return;
      }

      const matchingGroup = groups.find(
        (group) => group.canonicalProductId === requestCanonicalProductId,
      );
      if (!matchingGroup) {
        return;
      }

      const directionMultiplier = detailLoad?.direction
        && request?.requestDirection
        && detailLoad.direction !== request.requestDirection
        ? -1
        : 1;
      const absoluteRequestQuantity = getRequestProductQuantity(request);
      const absoluteRequestWeight = getRequestProductWeight(request);
      const requestQuantity = absoluteRequestQuantity * directionMultiplier;
      const requestWeight = absoluteRequestWeight * directionMultiplier;

      matchingGroup.measuredQuantity += requestQuantity;
      matchingGroup.measuredWeight += requestWeight;
    });

  return groups.map((group) => {
    const scaledQuantity = Math.max(group.measuredQuantity, 0);
    const scaledWeight = Math.max(group.measuredWeight, 0);
    const { bookedQuantity } = group;
    const targetWeight = group.hasTargetWeight ? group.targetWeight : null;

    return {
      id: group.id,
      canonicalProductId: group.canonicalProductId,
      productName: [...group.productNames.values()].join(', '),
      brandName: [...group.brandNames.values()].join(', '),
      size: [...group.sizes.values()].join(', '),
      packagePerPallet: group.packagePerPallet,
      bookedQuantity,
      palletQuantity: group.palletQuantity,
      scaledQuantity,
      targetWeight,
      scaledWeight,
    };
  });
}

export function LoadDetailModal({
  detailLoadId,
  detailLoad,
  detailLoadState,
  detailLoadSummary,
  requests = [],
  requestLoads = [],
  onUpdateStatus,
  onVoidRequest,
  onClose,
}) {
  if (!detailLoadId) {
    return null;
  }

  const isFinal = detailLoad?.status === 'Completed' || detailLoad?.status === 'Cancelled';
  const requestById = new Map(
    requests
      .filter((request) => Boolean(request?.requestId))
      .map((request) => [request.requestId, request]),
  );
  const requestLoadById = new Map(
    requestLoads
      .filter((requestLoad) => Boolean(requestLoad?.loadId))
      .map((requestLoad) => [requestLoad.loadId, requestLoad]),
  );
  const detailRequests = Array.isArray(detailLoadSummary?.requests) ? detailLoadSummary.requests : [];
  const listedRequests = detailRequests;
  const progressRequests = buildProgressRequests(detailLoad, detailRequests, requests, requestLoads);
  const bookedItemProgress = buildBookedItemProgress(detailLoad?.bookedItems, progressRequests, detailLoad);
  const explicitBookedWeight = getBookedWeight(detailLoad);
  const inferredBookedWeight = bookedItemProgress.length > 0
    && bookedItemProgress.every((item) => item.targetWeight != null)
    ? bookedItemProgress.reduce((total, item) => total + item.targetWeight, 0)
    : null;
  const bookedWeight = explicitBookedWeight ?? inferredBookedWeight;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: 'min(100%, 760px)',
          maxHeight: '88vh',
          overflow: 'auto',
          background: '#fff',
          borderRadius: '24px',
          padding: '1.5rem',
          boxShadow: '0 20px 70px rgba(15, 23, 42, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <div className="kiosk-card-header">
          <div>
            <h2>{detailLoad?.name ?? 'Load detail'}</h2>
            <p>{detailLoad?.loadId ?? detailLoadId}</p>
          </div>
          <span className={`kiosk-pill kiosk-pill-${statusClass(detailLoad?.status ?? 'unknown')}`}>
            {formatRequestLoadStatus(detailLoad?.status)}
          </span>
        </div>

        {detailLoad ? (
          <>
            <dl className="kiosk-meta-grid">
              <div>
                <dt>ทิศทาง</dt>
                <dd>{detailLoad.direction === 'Inbound' ? 'ขาเข้า' : detailLoad.direction === 'Outbound' ? 'ขาออก' : 'ไม่ระบุ'}</dd>
              </div>
              <div>
                <dt>สร้างเมื่อ</dt>
                <dd>{formatDate(detailLoad.createdAtUtc)}</dd>
              </div>
              <div>
                <dt>น้ำหนักจองรวม</dt>
                <dd>{bookedWeight == null ? 'Not set' : formatWeight(bookedWeight)}</dd>
              </div>
          {/*    <div>
                <dt>Created by</dt>
                <dd>{detailLoad.createdBy || 'Unknown'}</dd>
              </div>
              <div>
                <dt>Requests</dt>
                <dd>{listedRequests.length}</dd>
              </div>
          */}
            </dl>
{/*
            {detailLoad.notes ? (
              <div className="kiosk-notes">
                <strong>Notes</strong>
                <p>{detailLoad.notes}</p>
              </div>
            ) : null}
*/}
            {bookedItemProgress.length > 0 ? (
              <section
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.85rem',
                }}
              >
                <div>
                  <strong>รายการสินค้าที่จอง</strong>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '0.9rem',
                  }}
                >
                  {bookedItemProgress.map((item) => {
                    const meta = [item.brandName].filter(Boolean).join(' · ');
                    const packProgressPercent = getProgressPercent(item.scaledQuantity, item.bookedQuantity);
                    const weightProgressPercent = getProgressPercent(item.scaledWeight, item.targetWeight);

                    return (
                      <article
                        key={item.id}
                        style={{
                          border: '1px solid rgba(70, 106, 125, 0.18)',
                          borderRadius: '18px',
                          padding: '1rem',
                          background: 'linear-gradient(180deg, #f8fcff 0%, #ffffff 100%)',
                          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.7rem',
                        }}
                      >
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1rem' }}>
                            {item.productName} ({item.packagePerPallet} แพ็ค/พาเลท)
                          </h3>
                          {meta ? (
                            <p style={{ margin: '0.2rem 0 0', color: '#466a7d', fontSize: '0.92rem' }}>{meta}</p>
                          ) : null}
                        </div>

                        <dl
                          style={{
                            margin: 0,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: '0.55rem 0.8rem',
                          }}
                        >
                          <div>
                            <dt style={{ color: '#6c8797', fontSize: '0.78rem' }}>จำนวนสแกน/จำนวนจอง</dt>
                            <dd style={{ margin: '0.18rem 0 0', fontWeight: 700 }}>
                              {Math.round(item.scaledQuantity * 100) / 100} / {item.bookedQuantity} packs
                            </dd>
                            <BookedItemProgressBar
                              label={`${item.productName} scanned packs progress`}
                              percent={packProgressPercent}
                              color="#2f6f93"
                              isOverTarget={item.scaledQuantity > item.bookedQuantity}
                            />
                          </div>
                          <div>
                            <dt style={{ color: '#6c8797', fontSize: '0.78rem' }}>น้ำหนักสแกน/น้ำหนักจอง</dt>
                            <dd style={{ margin: '0.18rem 0 0', fontWeight: 700 }}>
                              {formatWeight(item.scaledWeight)} / {item.targetWeight == null
                                ? 'Not set'
                                : formatWeight(item.targetWeight)}
                            </dd>
                            <BookedItemProgressBar
                              label={`${item.productName} scanned goods weight progress`}
                              percent={weightProgressPercent}
                              color="#7c5cbd"
                              isOverTarget={item.targetWeight != null && item.scaledWeight > item.targetWeight}
                            />
                          </div>
                        </dl>
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}
            <details key={detailLoadId}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 800,
                }}
              >
                <span>รายการชั่งน้ำหนัก ({listedRequests.length})</span>
              </summary>
              <div
                className="kiosk-reading-list"
                style={{ maxHeight: 'none', overflow: 'visible', marginTop: '0.75rem' }}
              >
              {listedRequests.length === 0 ? (
                <div className="kiosk-empty">
                  <strong>ยังไม่มีรายการชั่งน้ำหนัก</strong>
                </div>
              ) : listedRequests.map((request) => (
                <article key={request.requestId} className="kiosk-attempt-card">
                  <div className="kiosk-attempt-top">
                    <div>
                      <h3>{request.requestId}</h3>
                      <p>{request.registerId} · {formatDate(request.issuedAtUtc)}</p>
                    </div>
                    <span className={`kiosk-pill kiosk-pill-${statusClass(request.requestStatus)}`}>
                      {request.requestStatus}
                    </span>
                  </div>
                  {request.replacesRequestId ? (
                    <p className="kiosk-void-replacement-notice">
                      ย้ายมาจาก {getReplacementSourceLoadName(request, requestById, requestLoadById)}
                    </p>
                  ) : null}
                  <dl className="kiosk-meta-grid">
                    <div>
                      <dt>Direction</dt>
                      <dd>{formatRequestDirection(request.requestDirection ?? detailLoad.direction)}</dd>
                    </div>
                    <div>
                      <dt>Scanner</dt>
                      <dd>{request.palletValue ?? 'Pending'}</dd>
                    </div>
                    <div>
                      <dt>Product</dt>
                      <dd>{!request.palletProductName || request.palletProductName === '' ? 'Not found' : request.palletProductName}</dd>
                    </div>
                    {isCompletedRequest(request) || hasMatchedReadings(request) ? (
                      <div>
                        <dt>
                          {(request.requestDirection ?? detailLoad.direction) === 'Inbound'
                            ? 'จำนวนแพ็คที่นับ'
                            : 'จำนวนแพ็คที่สแกน'}
                        </dt>
                        <dd>{getRequestProductQuantity(request)} packs</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>Scale</dt>
                      <dd>{request.scaleValue ?? 'Pending'}</dd>
                    </div>
                    <div>
                      <dt>Calculated goods weight</dt>
                      <dd>
                        {request.calculatedGoodsWeightKg == null
                          ? 'Not available'
                          : formatWeight(request.calculatedGoodsWeightKg)}
                      </dd>
                    </div>
                  </dl>
                  {request.requestStatus === 'Completed' && onVoidRequest ? (
                    <div style={{ marginTop: '6px' }}>
                      <button
                        type="button"
                        className="kiosk-danger-btn"
                        style={{ fontSize: '0.78rem', padding: '4px 12px', minHeight: 'unset' }}
                        onClick={() => onVoidRequest(request.requestId)}
                      >
                        Void confirmation
                      </button>
                    </div>
                  ) : null}
                </article>
              ))}
              </div>
            </details>
          </>
        ) : null}

        {detailLoadState.message ? (
          <p className={`kiosk-feedback kiosk-feedback-${detailLoadState.type}`}>
            {detailLoadState.message}
          </p>
        ) : null}

        <div className="kiosk-actions">
          <button
            type="button"
            className="kiosk-primary-btn"
            disabled={!detailLoad || isFinal}
            onClick={() => onUpdateStatus('complete')}
          >
            จบการโหลดของ
          </button>
          <button
            type="button"
            className="kiosk-danger-btn"
            disabled={!detailLoad || isFinal}
            onClick={() => onUpdateStatus('cancel')}
          >
            ยกเลิกการโหลดของ
          </button>
          <button
            type="button"
            className="kiosk-secondary-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
}
